import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ProjectDeliveryMode,
  type ProjectGovernanceDto,
  type ProjectMilestone,
  type ProjectMilestoneDependencyDto,
  type ProjectScheduleDto,
} from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { useGovernanceMutation } from './useGovernanceMutation';
import { formatDate } from '@/utils/formatDate';

const DAY_MS = 86_400_000;

export function MilestoneSchedulePanel({
  projectId,
  milestones,
  schedule,
  dependencies,
  governance,
}: {
  projectId: string;
  milestones: ProjectMilestone[];
  schedule: ProjectScheduleDto | null | undefined;
  dependencies: ProjectMilestoneDependencyDto[];
  governance: ProjectGovernanceDto;
}) {
  const canEdit =
    governance.permissions.isProjectManager &&
    governance.state !== 'closed' &&
    governance.deliveryMode === ProjectDeliveryMode.Internal;

  return (
    <div className="mb-6 space-y-4">
      <CompactTimeline milestones={milestones} schedule={schedule} />
      <DependencyEditor
        projectId={projectId}
        milestones={milestones}
        dependencies={dependencies}
        governance={governance}
        canEdit={canEdit}
      />
    </div>
  );
}

function CompactTimeline({
  milestones,
  schedule,
}: {
  milestones: ProjectMilestone[];
  schedule: ProjectScheduleDto | null | undefined;
}) {
  const { t } = useTranslation();
  const nodes = schedule?.milestones ?? [];
  if (nodes.length === 0) return null;
  const starts = nodes.map((n) => Date.parse(n.earliestStart));
  const ends = nodes.map((n) => Date.parse(n.earliestFinish));
  const min = Math.min(...starts);
  const max = Math.max(...ends);
  const span = Math.max(max - min, DAY_MS);

  return (
    <div className="rounded-md border p-3 space-y-2">
      <h4 className="text-sm font-medium">{t('workspace.milestones.schedule.timeline')}</h4>
      {nodes.map((node) => {
        const title = milestones.find((m) => Number(m.id) === node.id)?.title ?? String(node.id);
        const left = ((Date.parse(node.earliestStart) - min) / span) * 100;
        const width = Math.max(
          ((Date.parse(node.earliestFinish) - Date.parse(node.earliestStart) + DAY_MS) / span) * 100,
          4,
        );
        return (
          <div key={node.id} className="space-y-1">
            <div className="flex justify-between gap-2 text-xs text-muted-foreground">
              <span className="truncate">{title}</span>
              <span>
                {formatDate(node.earliestStart)} – {formatDate(node.earliestFinish)}
              </span>
            </div>
            <div className="relative h-2 rounded bg-muted">
              <div
                className={`absolute h-2 rounded ${node.isCritical ? 'bg-destructive' : 'bg-primary/60'}`}
                style={{ left: `${left}%`, width: `${width}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DependencyEditor({
  projectId,
  milestones,
  dependencies,
  governance,
  canEdit,
}: {
  projectId: string;
  milestones: ProjectMilestone[];
  dependencies: ProjectMilestoneDependencyDto[];
  governance: ProjectGovernanceDto;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const [predecessorId, setPredecessorId] = useState('');
  const [successorId, setSuccessorId] = useState('');
  const [lag, setLag] = useState('0');
  const titleOf = useMemo(
    () => new Map(milestones.map((m) => [Number(m.id), m.title])),
    [milestones],
  );

  const save = useGovernanceMutation(
    projectId,
    (next: { predecessorMilestoneId: number; successorMilestoneId: number; lagWorkingDays: number }[]) =>
      projectGovernanceApi.setMilestoneDependencies(projectId, {
        expectedVersion: governance.version,
        dependencies: next,
      }),
    () => {
      setPredecessorId('');
      setSuccessorId('');
      setLag('0');
    },
  );

  const add = () => {
    const pred = Number(predecessorId);
    const succ = Number(successorId);
    if (!pred || !succ || pred === succ) return;
    save.mutate([
      ...dependencies.map((d) => ({
        predecessorMilestoneId: d.predecessorMilestoneId,
        successorMilestoneId: d.successorMilestoneId,
        lagWorkingDays: d.lagWorkingDays,
      })),
      { predecessorMilestoneId: pred, successorMilestoneId: succ, lagWorkingDays: Number(lag) || 0 },
    ]);
  };

  const remove = (index: number) => {
    save.mutate(
      dependencies
        .filter((_, i) => i !== index)
        .map((d) => ({
          predecessorMilestoneId: d.predecessorMilestoneId,
          successorMilestoneId: d.successorMilestoneId,
          lagWorkingDays: d.lagWorkingDays,
        })),
    );
  };

  return (
    <div className="rounded-md border p-3 space-y-3">
      <h4 className="text-sm font-medium">{t('workspace.milestones.schedule.dependencies')}</h4>
      {dependencies.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('workspace.milestones.schedule.dependenciesEmpty')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {dependencies.map((d, index) => (
            <li key={d.id} className="flex flex-wrap items-center gap-2">
              <span>
                {titleOf.get(d.predecessorMilestoneId) ?? d.predecessorMilestoneId}
                {' → '}
                {titleOf.get(d.successorMilestoneId) ?? d.successorMilestoneId}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('workspace.milestones.schedule.lagDays', { days: d.lagWorkingDays })}
              </span>
              {canEdit && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={save.isPending}
                  onClick={() => remove(index)}
                >
                  {t('workspace.milestones.schedule.removeDependency')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && milestones.length >= 2 && (
        <div className="grid gap-2 sm:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">{t('workspace.milestones.schedule.predecessor')}</Label>
            <Select value={predecessorId} onValueChange={setPredecessorId}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder={t('workspace.milestones.schedule.selectMilestone')} />
              </SelectTrigger>
              <SelectContent>
                {milestones.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('workspace.milestones.schedule.successor')}</Label>
            <Select value={successorId} onValueChange={setSuccessorId}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder={t('workspace.milestones.schedule.selectMilestone')} />
              </SelectTrigger>
              <SelectContent>
                {milestones.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('workspace.milestones.schedule.lag')}</Label>
            <Input className="h-8" type="number" value={lag} onChange={(e) => setLag(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              size="sm"
              disabled={!predecessorId || !successorId || predecessorId === successorId || save.isPending}
              onClick={add}
            >
              {t('workspace.milestones.schedule.addDependency')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
